import { useMemo } from 'react';
//-tech-debt-react-query;
import { useQueries } from '@tanstack/react-query';

import { fetchItems } from '../support/queries';
import {
  SF_ID_CHUNK,
  chunk,
  sanitizeSfIds,
  soqlInById,
} from '../support/salesforceBatch';

/**
 * Batch-resolve Salesforce records by Id through the raw SOQL passthrough (`sf/query`), one request per
 * chunk of ~200 ids, instead of fanning out one scalar `sf/<object>/<id>` request per id.
 *
 * This is the shared engine behind `useCustomerNames`, `useCampaignPipeline`, etc. The structured list
 * filters silently drop an `Id` anyOf clause ([[salesforce-id-list-filters-are-dropped]]), so a batch
 * must go through `WHERE Id IN (...)` on `sf/query`; the pure helpers in `support/salesforceBatch.ts`
 * do the sanitising (which also makes the interpolated IN list injection-safe) and chunking.
 *
 * The returned map is keyed by BOTH the full returned `Id` and its 15-char prefix, so a caller holding
 * a 15-char id resolves an 18-char record (and vice versa) — use `getSfRecord` to read it. A failed
 * chunk is not retried and not surfaced as an error: an id the SE cannot see in Salesforce (or a lapsed
 * session) simply has no entry, and callers fall back to the id itself as a label.
 */
export interface SfRecordsById<T = any> {
  /** Keyed by full 18-char Id and by 15-char prefix; read via `getSfRecord`. */
  byId: Map<string, T>;
  loading: boolean;
}

export interface UseSfRecordsByIdOptions {
  /** Cache-key prefix so distinct callers (opportunities vs accounts) don't collide in react-query. */
  cacheKey: string;
  /** How long a chunk stays fresh; defaults to react-query's own default when omitted. */
  staleTime?: number;
  /** Skip fetching entirely (e.g. a gate not yet satisfied). */
  enabled?: boolean;
}

export function useSfRecordsById<T = any>(
  object: string,
  fields: string[],
  rawIds: (string | null | undefined)[],
  options: UseSfRecordsByIdOptions,
): SfRecordsById<T> {
  const { cacheKey, staleTime, enabled = true } = options;

  // `Id` must be selected so the response can be keyed; fold it in without duplicating it.
  const selectFields = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const f of ['Id', ...fields]) {
      const trimmed = f.trim();
      const lower = trimmed.toLowerCase();
      if (trimmed && !seen.has(lower)) {
        seen.add(lower);
        out.push(trimmed);
      }
    }
    return out;
  }, [fields]);

  // Sanitise + de-dupe, then key off the joined id string so a fresh inline array on every render does
  // not rebuild the query list (and the returned map) each render.
  const idKey = useMemo(() => sanitizeSfIds(rawIds).join(','), [rawIds]);
  const ids = useMemo(() => (idKey ? idKey.split(',') : []), [idKey]);

  const chunks = useMemo(() => chunk(ids, SF_ID_CHUNK), [ids]);

  const queries = useQueries({
    queries: chunks.map((batch) => ({
      queryKey: [cacheKey, 'sf-batch', object, selectFields.join(','), batch.join(',')],
      queryFn: () => fetchItems('sf/query', { q: soqlInById(object, selectFields, batch) }),
      enabled: enabled && batch.length > 0,
      ...(staleTime != null ? { staleTime } : {}),
      // One unreachable/unauthorised chunk should not retry three times.
      retry: false as const,
    })),
  });

  return useMemo(() => {
    const byId = new Map<string, T>();
    for (const q of queries) {
      const records = (q.data as any[]) ?? [];
      for (const rec of records) {
        const id = rec?.Id;
        if (!id) continue;
        byId.set(String(id), rec as T);
        // Also key by the 15-char prefix so a 15↔18 form mismatch still resolves via getSfRecord.
        if (String(id).length >= 15) byId.set(String(id).slice(0, 15), rec as T);
      }
    }
    return { byId, loading: enabled && queries.some((q) => q.isLoading) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, queries.map((q) => q.dataUpdatedAt).join(',')]);
}

export default useSfRecordsById;
